import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { collection, collectionGroup, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

import { auth, db, functions, storage } from "../firebaseConfig";
import Header from "../components/HeaderV2";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/PointV2.module.css";
import ErrMsg from "../ErrMsg";
import { PhotoInput, SelectorInput, ToggleSwitch, photoResize } from "../components/Inputs";
import { PointType, usePoint } from "../PointContext";
import { useMember } from "../MemberContext";
import { PartnerType, usePartner } from "../PartnerContext";
import { useAuth } from "../AuthContext";
import { ImageIcon, Trash2 } from "lucide-react";

export interface MemberType {
    uid: string;
    name: string;
    phone: string;
    deleted: boolean;
    bankAccount: any[];
    savedKiosk: any[];
    timestamp: number;
    partnerId: string;
}

export interface WithdrawMethodType {
    id: string;
    title: string;
    amount: number | string;
    type?: string;
    image?: string;
    active: boolean;
    timestamp?: number;
    createdAt?: number;
    createdBy?: string;
    createdByEmail?: string | null;
    updatedAt?: number;
    updatedBy?: string;
    updatedByEmail?: string | null;
    partnerId: string;
    partnerName: string;
}

type PointView = "withdraws" | "methods";

const Point = ({ view = "withdraws" }: { view?: PointView }) => {
    const { withdraws } = usePoint();
    const { members } = useMember();
    const { partner } = usePartner();
    const { userType } = useAuth();
    const { withdrawMethods } = useWithdrawMethods(partner);
    const [addPoint, setAddPoint] = useState<boolean>(false);
    const [addWithdraw, setAddWithdraw] = useState<string | null>(null);
    const [editWithdraw, setEditWithdraw] = useState<WithdrawMethodType | null>(null);
    const [updatingMethodMap, setUpdatingMethodMap] = useState<Record<string, boolean>>({});

    const totalWithdrawAmount = withdraws.reduce((sum, point) => sum + Math.abs(Number(point.pointDelta || 0)), 0);
    const partnersWithoutWithdraw = partner.filter((item) => !withdrawMethods.some((method) => method.partnerId === item.id)).length;
    const activeMethodCount = withdrawMethods.filter((method) => method.active).length;
    const canOperateWithdrawMethods = userType === "SUPER_ADMIN";
    const isMethodView = view === "methods";

    const toggleWithdrawMethod = async (method: WithdrawMethodType) => {
        if (!canOperateWithdrawMethods) return alert("총괄 관리자만 환전 방법을 대리운영할 수 있습니다.");

        const updateKey = getWithdrawMethodKey(method);
        setUpdatingMethodMap((prev) => ({ ...prev, [updateKey]: true }));
        try {
            await updateDoc(doc(db, `partner/${method.partnerId}/withdraw`, method.id), {
                active: !method.active,
                updatedAt: Date.now(),
                ...getActorMeta("updated"),
            });
        } catch (err) {
            console.error(err);
            alert(getFirebaseErrorMessage(err, "환전 방법 노출 상태 변경 중 오류가 발생했습니다."));
        } finally {
            setUpdatingMethodMap((prev) => ({ ...prev, [updateKey]: false }));
        }
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>포인트 관리</h4>
                    <p className={common["text--copy"]}>
                        {isMethodView
                            ? "파트너사별 환전 방법과 회원 화면 노출 상태를 관리합니다."
                            : "포인트 지급과 대기 중인 환전 요청을 관리합니다."}
                    </p>

                    <div className={style["summary-grid"]}>
                        {isMethodView ? (
                            <>
                                <SummaryCard label="파트너사" value={`${partner.length}개`} />
                                <SummaryCard label="환전 방법" value={`${withdrawMethods.length}개`} />
                                <SummaryCard label="노출 중" value={`${activeMethodCount}개`} />
                                <SummaryCard label="방법 미등록" value={`${partnersWithoutWithdraw}개`} />
                            </>
                        ) : (
                            <>
                                <SummaryCard label="환전 대기" value={`${withdraws.length}건`} />
                                <SummaryCard label="대기 요청 금액" value={`₩${totalWithdrawAmount.toLocaleString()}`} />
                                <SummaryCard label="전체 회원" value={`${members.length}명`} />
                                <SummaryCard label="파트너사" value={`${partner.length}개`} />
                            </>
                        )}
                    </div>

                    {isMethodView ? (
                        <WithdrawMethodPanel
                            methods={withdrawMethods}
                            partners={partner}
                            canOperate={canOperateWithdrawMethods}
                            onAdd={(partnerId) => setAddWithdraw(partnerId)}
                            onEdit={(withdraw) => setEditWithdraw(withdraw)}
                            onToggle={toggleWithdrawMethod}
                            updatingMap={updatingMethodMap}
                        />
                    ) : (
                        <>
                            <section className={style["action-panel"]}>
                                <div>
                                    <p className={style["section-kicker"]}>수동 지급</p>
                                    <h5>포인트 지급</h5>
                                    <p>파트너사와 회원을 선택한 뒤 지정한 포인트를 지급합니다.</p>
                                </div>
                                <button className={style["button--primary"]} onClick={() => setAddPoint(true)}>
                                    포인트 지급
                                </button>
                            </section>

                            <PointTable />
                        </>
                    )}
                </div>
            </div>

            {addPoint && <AddPoint close={() => setAddPoint(false)} />}
            {addWithdraw !== null && (
                <WithdrawMethodModal
                    mode="CREATE"
                    partners={partner}
                    defaultPartnerId={addWithdraw}
                    close={() => setAddWithdraw(null)}
                />
            )}
            {editWithdraw && (
                <WithdrawMethodModal
                    mode="EDIT"
                    partners={partner}
                    withdraw={editWithdraw}
                    close={() => setEditWithdraw(null)}
                />
            )}
        </>
    );
};

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
    <div className={style["summary-card"]}>
        <p>{label}</p>
        <strong>{value}</strong>
    </div>
);

const PointTable = () => {
    const { withdraws } = usePoint();
    const { partner } = usePartner();
    const [search, setSearch] = useState<string>("");
    const [selected, setSelected] = useState<PointType | null>(null);
    const [loadingConfirmMap, setLoadingConfirmMap] = useState<Record<string, boolean>>({});

    const partnerNameMap = useMemo(
        () =>
            partner.reduce<Record<string, string>>((acc, item) => {
                acc[item.id || item.docId] = item.name;
                return acc;
            }, {}),
        [partner]
    );

    const getPartnerName = (point: PointType) => {
        const partnerId = point.member?.partnerId || point.partnerId;
        return partnerNameMap[partnerId] || partnerId || "-";
    };

    const filteredWithdraws = withdraws.filter((point) => {
        const keyword = search.toLowerCase();
        const name = point.member?.name ?? "";
        const phone = point.member?.phone ?? "";
        const partnerName = getPartnerName(point);
        return (
            search === "" ||
            name.toLowerCase().includes(keyword) ||
            phone.toLowerCase().includes(keyword) ||
            formatPhone(phone).toLowerCase().includes(keyword) ||
            partnerName.toLowerCase().includes(keyword)
        );
    });

    const filteredAmount = filteredWithdraws.reduce((sum, point) => sum + Math.abs(Number(point.pointDelta || 0)), 0);

    const confirmWithdraw = async (point: PointType) => {
        if (!window.confirm("환전 지급 완료 처리하시겠습니까?")) return;

        setLoadingConfirmMap((prev) => ({ ...prev, [point.id]: true }));

        return httpsCallable(
            functions,
            "completeWithdraw"
        )({ docId: point.id })
            .then(() => {
                setLoadingConfirmMap((prev) => ({ ...prev, [point.id]: false }));
            })
            .catch((err) => {
                console.error(err);
                setLoadingConfirmMap((prev) => ({ ...prev, [point.id]: false }));

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <section className={style["point-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>환전 승인</p>
                    <h5 className={style["panel-title"]}>대기 중인 환전 요청</h5>
                    <p className={style["panel-copy"]}>
                        {filteredWithdraws.length}건 · ₩{filteredAmount.toLocaleString()}
                    </p>
                </div>
            </div>
            <div className={style["toolbar"]}>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="이름 또는 전화번호로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className={style["wrapper--point-list"]}>
                <div className={[style["point-row"], style["point-row--head"]].join(" ")}>
                    <p>이름</p>
                    <p>파트너사</p>
                    <p>전화번호</p>
                    <p>요청일시</p>
                    <p>요청금액</p>
                    <p>관리</p>
                </div>
                {filteredWithdraws.length === 0 && <div className={style["empty-state"]}>대기 중인 환전 요청이 없습니다.</div>}
                {filteredWithdraws.map((point) => (
                    <PointRow
                        key={point.id}
                        point={point}
                        partnerName={getPartnerName(point)}
                        loading={loadingConfirmMap[point.id] ?? false}
                        onPay={() => confirmWithdraw(point)}
                        onOpen={() => setSelected(point)}
                    />
                ))}
            </div>

            {selected && <WithdrawDetail point={selected} partnerName={getPartnerName(selected)} close={() => setSelected(null)} />}
        </section>
    );
};

const PointRow = ({
    point,
    partnerName,
    loading,
    onPay,
    onOpen,
}: {
    point: PointType;
    partnerName: string;
    loading: boolean;
    onPay: () => void;
    onOpen: () => void;
}) => (
    <div className={style["unit--point-list"]} onClick={onOpen}>
        <div className={style["point-row"]}>
            <p className={style["row-strong"]}>{point.member?.name ?? "이름 없음"}</p>
            <p>{partnerName}</p>
            <p>{formatPhone(point.member?.phone) || "-"}</p>
            <p>{formatDateTime(point.timestamp)}</p>
            <p className={style["amount"]}>₩{Math.abs(Number(point.pointDelta || 0)).toLocaleString()}</p>
            <div className={style["row-actions"]}>
                <button
                    className={style["button--pay"]}
                    disabled={loading}
                    onClick={(e) => {
                        e.stopPropagation();
                        onPay();
                    }}
                >
                    {loading ? "처리 중" : "완료처리"}
                </button>
                <button
                    className={style["button--ghost"]}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpen();
                    }}
                >
                    상세
                </button>
            </div>
        </div>
    </div>
);

const WithdrawDetail = ({ point, partnerName, close }: { point: PointType; partnerName: string; close: () => void }) => {
    const [active, setActive] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    const confirmWithdraw = async () => {
        setLoading(true);

        return httpsCallable(
            functions,
            "completeWithdraw"
        )({ docId: point.id })
            .then(() => {
                setLoading(false);
                hide();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }
            });
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>환전 요청 상세</p>
                        <h4 className={style["modal-title"]}>{point.member?.name ?? "이름 없음"}</h4>
                        <p className={style["modal-copy"]}>₩{Math.abs(Number(point.pointDelta || 0)).toLocaleString()}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>요청 정보</h5>
                            <p>승인 전 사용자와 요청 금액을 확인합니다.</p>
                        </div>
                        <div className={style["detail-grid"]}>
                            <InfoItem label="이름" value={point.member?.name ?? "이름 없음"} />
                            <InfoItem label="파트너사" value={partnerName} />
                            <InfoItem label="전화번호" value={formatPhone(point.member?.phone) || "-"} />
                            <InfoItem label="요청일시" value={formatDateTime(point.timestamp)} />
                            <InfoItem label="요청금액" value={`₩${Math.abs(Number(point.pointDelta || 0)).toLocaleString()}`} />
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>계좌 정보</h5>
                            <p>등록된 계좌 정보가 있으면 출금 승인 전 확인합니다.</p>
                        </div>
                        <div className={style["bank-box"]}>{formatBankAccount(point.member?.bankAccount)}</div>
                    </div>

                    <div className={[style["modal-section"], style["confirm-zone"]].join(" ")}>
                        <div className={style["section-head"]}>
                            <h5>환전 완료 처리</h5>
                            <p>실제 송금 완료 후 승인 처리하세요.</p>
                        </div>
                        <CompleteWithdrawButton loading={loading} onClick={confirmWithdraw} />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

const CompleteWithdrawButton = ({ loading, onClick }: { loading: boolean; onClick: () => void }) => {
    const [confirming, setConfirming] = useState<boolean>(false);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!confirming) return;

        const close = (event: MouseEvent) => {
            if (!buttonRef.current?.contains(event.target as Node)) {
                setConfirming(false);
            }
        };

        document.body.addEventListener("click", close);
        return () => document.body.removeEventListener("click", close);
    }, [confirming]);

    return (
        <div ref={buttonRef}>
            <button
                type="button"
                className={[style["button--complete"], confirming && style["button--complete--confirm"]].filter(Boolean).join(" ")}
                disabled={loading}
                onClick={() => {
                    if (!confirming) {
                        setConfirming(true);
                        return;
                    }
                    onClick();
                    setConfirming(false);
                }}
            >
                {loading ? "처리 중" : confirming ? "완료 승인하기" : "완료 처리하기"}
            </button>
        </div>
    );
};

const WithdrawMethodPanel = ({
    methods,
    partners,
    canOperate,
    onAdd,
    onEdit,
    onToggle,
    updatingMap,
}: {
    methods: WithdrawMethodType[];
    partners: PartnerType[];
    canOperate: boolean;
    onAdd: (partnerId: string) => void;
    onEdit: (withdraw: WithdrawMethodType) => void;
    onToggle: (withdraw: WithdrawMethodType) => void;
    updatingMap: Record<string, boolean>;
}) => {
    const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>("ALL");
    const [search, setSearch] = useState<string>("");

    const filteredMethods = useMemo(() => {
        const keyword = search.toLowerCase();

        return methods.filter((method) => {
            const matchesPartner = !selectedPartnerId || method.partnerId === selectedPartnerId;
            const matchesStatus =
                statusFilter === "ALL" ||
                (statusFilter === "ACTIVE" && method.active) ||
                (statusFilter === "INACTIVE" && !method.active);
            const matchesSearch =
                !search ||
                method.partnerName.toLowerCase().includes(keyword) ||
                method.partnerId.toLowerCase().includes(keyword) ||
                method.title.toLowerCase().includes(keyword);

            return matchesPartner && matchesStatus && matchesSearch;
        });
    }, [methods, search, selectedPartnerId, statusFilter]);

    const activeCount = filteredMethods.filter((method) => method.active).length;

    return (
        <section className={style["point-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>환전 방법 대리운영</p>
                    <h5 className={style["panel-title"]}>파트너사별 등록된 환전 방법</h5>
                    <p className={style["panel-copy"]}>
                        총 {filteredMethods.length}개 · 노출 중 {activeCount}개
                    </p>
                </div>
                <button
                    className={style["button--primary"]}
                    disabled={!canOperate}
                    onClick={() => onAdd(selectedPartnerId)}
                >
                    환전 방법 추가
                </button>
            </div>
            <div className={[style["toolbar"], style["method-toolbar"]].join(" ")}>
                <SelectorInput placeholder="파트너사 전체" value={selectedPartnerId} setValue={setSelectedPartnerId}>
                    {partners.map((item) => (
                        <option key={item.docId || item.id} value={item.id}>
                            {item.name}
                        </option>
                    ))}
                </SelectorInput>
                <SelectorInput placeholder="상태 전체" value={statusFilter} setValue={setStatusFilter}>
                    <option value="ALL">전체</option>
                    <option value="ACTIVE">노출 중</option>
                    <option value="INACTIVE">미노출</option>
                </SelectorInput>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="파트너사명, 아이디 또는 환전 방법명으로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            {!canOperate && (
                <div className={style["permission-note"]}>
                    일반 관리자는 조회만 가능합니다. 등록, 수정, 삭제는 총괄 관리자 권한으로 처리할 수 있습니다.
                </div>
            )}
            <div className={style["wrapper--point-list"]}>
                <div className={[style["method-row"], style["point-row--head"]].join(" ")}>
                    <p>파트너사</p>
                    <p>이미지</p>
                    <p>제목</p>
                    <p>금액</p>
                    <p>상태</p>
                    <p>수정일</p>
                    <p>관리</p>
                </div>
                {filteredMethods.length === 0 && <div className={style["empty-state"]}>등록된 환전 방법이 없습니다.</div>}
                {filteredMethods.map((method) => (
                    <div key={`${method.partnerId}-${method.id}`} className={style["unit--point-list"]}>
                        <div className={style["method-row"]}>
                            <div className={style["method-partner"]}>
                                <strong>{method.partnerName}</strong>
                                <span>{method.partnerId}</span>
                            </div>
                            <div className={style["method-thumb"]}>
                                {method.image ? <img src={method.image} alt={method.title} /> : <span>이미지 없음</span>}
                            </div>
                            <p className={style["row-strong"]}>{method.title}</p>
                            <p className={style["amount"]}>₩{Number(method.amount || 0).toLocaleString()}</p>
                            <div className={style["method-switch"]}>
                                <ToggleSwitch
                                    checked={method.active}
                                    disabled={!canOperate || (updatingMap[getWithdrawMethodKey(method)] ?? false)}
                                    onChange={() => onToggle(method)}
                                />
                            </div>
                            <p>{formatDateTime(method.updatedAt || method.timestamp || method.createdAt)}</p>
                            <div className={style["row-actions"]}>
                                <button
                                    className={style["button--ghost"]}
                                    disabled={!canOperate}
                                    onClick={() => onEdit(method)}
                                >
                                    관리
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const WithdrawMethodModal = ({
    mode,
    partners,
    defaultPartnerId,
    withdraw,
    close,
}: {
    mode: "CREATE" | "EDIT";
    partners: PartnerType[];
    defaultPartnerId?: string;
    withdraw?: WithdrawMethodType;
    close: () => void;
}) => {
    const init = useRef(true);
    const [selectedPartnerId, setSelectedPartnerId] = useState<string>(withdraw?.partnerId || defaultPartnerId || "");
    const [partnerError, setPartnerError] = useState<string>("");
    const [title, setTitle] = useState<string>(withdraw?.title || "");
    const [titleError, setTitleError] = useState<string>("");
    const [amount, setAmount] = useState<string>(withdraw?.amount ? String(withdraw.amount) : "");
    const [amountError, setAmountError] = useState<string>("");
    const [activeFlag, setActiveFlag] = useState<boolean>(withdraw?.active ?? true);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoError, setPhotoError] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    const selectedPartner = partners.find((item) => item.id === selectedPartnerId);

    useEffect(() => setActive(true), []);
    useEffect(() => {
        if (init.current) return;
        setPartnerError(!selectedPartnerId ? "파트너사를 선택해주세요." : "");
    }, [selectedPartnerId]);
    useEffect(() => {
        if (init.current) return;
        setTitleError(!title.trim() ? "제목을 입력해주세요." : "");
    }, [title]);
    useEffect(() => {
        if (init.current) return;
        setAmountError(!amount || Number(amount) <= 0 ? "올바른 금액을 입력해주세요." : "");
    }, [amount]);
    useEffect(() => {
        init.current = false;
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(close, 520);
    };

    const saveWithdraw = async () => {
        if (!selectedPartnerId) return setPartnerError("파트너사를 선택해주세요.");
        if (!title.trim()) return setTitleError("제목을 입력해주세요.");
        if (!amount || Number(amount) <= 0) return setAmountError("올바른 금액을 입력해주세요.");
        if (mode === "CREATE" && !photo) return setPhotoError("이미지를 업로드해주세요.");

        setLoading(true);
        try {
            const now = Date.now();
            const docRef =
                mode === "EDIT" && withdraw
                    ? doc(db, `partner/${withdraw.partnerId}/withdraw`, withdraw.id)
                    : doc(collection(db, `partner/${selectedPartnerId}/withdraw`));

            let imageUrl = withdraw?.image || "";
            const targetPartnerId = mode === "EDIT" && withdraw ? withdraw.partnerId : selectedPartnerId;

            if (photo) {
                const resized = await photoResize(photo, "0");
                const storageRef = ref(storage, `partner/${targetPartnerId}/withdraw/${docRef.id}.png`);
                const uploadTask = uploadBytesResumable(storageRef, resized);

                await new Promise<void>((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => setProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                        reject,
                        () => resolve()
                    );
                });

                imageUrl = await getDownloadURL(ref(storage, `partner/${targetPartnerId}/withdraw/${docRef.id}.png`));
            }

            if (mode === "CREATE") {
                await setDoc(docRef, {
                    id: docRef.id,
                    title: title.trim(),
                    amount: Number(amount),
                    type: "COUPON",
                    active: activeFlag,
                    image: imageUrl,
                    timestamp: now,
                    createdAt: now,
                    updatedAt: now,
                    ...getActorMeta("created"),
                    ...getActorMeta("updated"),
                });
            } else {
                await updateDoc(docRef, {
                    title: title.trim(),
                    amount: Number(amount),
                    active: activeFlag,
                    image: imageUrl,
                    updatedAt: now,
                    ...getActorMeta("updated"),
                });
            }

            hide();
        } catch (err) {
            console.error(err);
            alert(getFirebaseErrorMessage(err, "환전 방법 저장 중 오류가 발생했습니다."));
        } finally {
            setLoading(false);
        }
    };

    const deleteWithdraw = async () => {
        if (!withdraw || !window.confirm("환전 방법을 삭제하시겠습니까?")) return;

        setLoadingDelete(true);
        try {
            if (withdraw.image) {
                await deleteObject(ref(storage, `partner/${withdraw.partnerId}/withdraw/${withdraw.id}.png`)).catch(() => {});
            }
            await deleteDoc(doc(db, `partner/${withdraw.partnerId}/withdraw`, withdraw.id));
            hide();
        } catch (err) {
            console.error(err);
            alert(getFirebaseErrorMessage(err, "환전 방법 삭제 중 오류가 발생했습니다."));
        } finally {
            setLoadingDelete(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div
                className={[
                    common["unit--overlay"],
                    style["modal"],
                    style["modal--withdraw"],
                    active && common["unit--overlay--active"],
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>환전 방법 대리운영</p>
                        <h4 className={style["modal-title"]}>{mode === "CREATE" ? "환전 방법 추가" : "환전 방법 수정"}</h4>
                        <p className={style["modal-copy"]}>
                            {mode === "CREATE"
                                ? "총괄 관리자가 파트너사를 대신해 회원에게 노출될 환전 방법을 등록합니다."
                                : `${withdraw?.partnerName || selectedPartner?.name || "-"}의 환전 방법을 수정합니다.`}
                        </p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["withdraw-form-grid"]}>
                        <div className={style["modal-section"]}>
                            <div className={style["section-head"]}>
                                <h5>기본 정보</h5>
                                <p>회원에게 표시될 이름과 필요 포인트를 입력합니다.</p>
                            </div>
                            {mode === "CREATE" ? (
                                <SelectorInput
                                    placeholder="파트너사 선택"
                                    value={selectedPartnerId}
                                    setValue={(value) => {
                                        setSelectedPartnerId(value);
                                        setPartnerError("");
                                    }}
                                    error={partnerError}
                                >
                                    {partners.map((item) => (
                                        <option key={item.docId || item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </SelectorInput>
                            ) : (
                                <div className={style["detail-grid"]}>
                                    <InfoItem label="파트너사" value={withdraw?.partnerName || "-"} />
                                    <InfoItem label="파트너사 ID" value={withdraw?.partnerId || "-"} />
                                </div>
                            )}
                            <input
                                className={style["input--member-point"]}
                                type="text"
                                placeholder="환전 방법명"
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    setTitleError("");
                                }}
                            />
                            {titleError && <p className={style["form-error"]}>{titleError}</p>}
                            <input
                                className={style["input--member-point"]}
                                type="number"
                                placeholder="필요 포인트(금액)"
                                value={amount}
                                onChange={(e) => {
                                    setAmount(e.target.value);
                                    setAmountError("");
                                }}
                            />
                            {amountError && <p className={style["form-error"]}>{amountError}</p>}
                            <div className={style["method-active-control"]}>
                                <div>
                                    <strong>회원 화면 노출</strong>
                                    <span>{activeFlag ? "노출 중" : "미노출"}</span>
                                </div>
                                <ToggleSwitch checked={activeFlag} onChange={() => setActiveFlag((prev) => !prev)} />
                            </div>
                        </div>

                        <div className={[style["modal-section"], style["photo-panel"]].join(" ")}>
                            <div className={style["photo-title"]}>
                                <ImageIcon size={18} />
                                <h5>대표 이미지</h5>
                            </div>
                            <div className={style["photo-input-wrap"]}>
                                <PhotoInput
                                    photo={photo}
                                    setPhoto={(file) => {
                                        setPhoto(file);
                                        setPhotoError("");
                                    }}
                                    progress={progress}
                                    error={photoError}
                                    existingImageUrl={withdraw?.image}
                                />
                            </div>
                        </div>
                    </div>

                    {mode === "EDIT" && (
                        <div className={[style["modal-section"], style["danger-zone"]].join(" ")}>
                            <div className={style["section-head"]}>
                                <h5>환전 방법 삭제</h5>
                                <p>삭제하면 회원 화면의 선택지에서도 즉시 사라집니다.</p>
                            </div>
                            <button className={style["button--danger"]} onClick={deleteWithdraw} disabled={loadingDelete}>
                                <Trash2 size={16} />
                                {loadingDelete ? "삭제 중" : "삭제하기"}
                            </button>
                        </div>
                    )}
                </div>

                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={saveWithdraw} disabled={loading}>
                        {loading ? "저장 중" : "저장"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AddPoint = ({ close }: { close: () => void }) => {
    const { members } = useMember();
    const { partner } = usePartner();

    const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
    const [selectedMember, setSelectedMember] = useState<MemberType | null>(null);
    const [search, setSearch] = useState<string>("");
    const [point, setPoint] = useState<number | null>(null);
    const [message, setMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    const filteredMembers = useMemo(() => {
        if (!selectedPartnerId) return [];
        const keyword = search.toLowerCase();
        return members
            .filter((member) => member.partnerId === selectedPartnerId)
            .filter((member) => {
                return (
                    search === "" ||
                    member.partnerId.toLowerCase().includes(keyword) ||
                    member.name.toLowerCase().includes(keyword) ||
                    member.phone.toLowerCase().includes(keyword) ||
                    formatPhone(member.phone).toLowerCase().includes(keyword)
                );
            });
    }, [members, selectedPartnerId, search]);

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);

    const hide = useCallback(() => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    }, [close]);

    const givePoint = async () => {
        setMessage("");

        if (!selectedPartnerId) {
            setMessage("파트너사를 선택해주세요.");
            return;
        }
        if (!selectedMember) {
            setMessage("포인트를 지급할 회원을 선택해주세요.");
            return;
        }
        if (!point || point <= 0) {
            setMessage("지급할 포인트를 입력해주세요.");
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "addPoint"
        )({ uid: selectedMember.uid, pointDelta: point, partnerId: selectedPartnerId })
            .then(() => {
                setPoint(null);
                setSelectedMember(null);
                setMessage("포인트가 지급되었습니다.");
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);

                if (ErrMsg[err.message]) {
                    setMessage(ErrMsg[err.message]);
                } else {
                    setMessage("에러가 발생했습니다.");
                }
            });
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>수동 지급</p>
                        <h4 className={style["modal-title"]}>포인트 지급</h4>
                        <p className={style["modal-copy"]}>파트너사와 회원을 선택한 뒤 포인트를 지급합니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>파트너사 선택</h5>
                            <p>선택한 파트너사의 회원만 표시됩니다.</p>
                        </div>
                        <SelectorInput
                            placeholder="파트너사 선택"
                            value={selectedPartnerId || ""}
                            setValue={(value) => {
                                setSelectedPartnerId(value);
                                setSelectedMember(null);
                            }}
                        >
                            {partner.map((p) => (
                                <option key={p.docId} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </SelectorInput>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>회원 선택</h5>
                            <p>지급할 회원을 한 명 선택합니다.</p>
                        </div>
                        {selectedPartnerId ? (
                            <>
                                <input
                                    className={style["input--member-filter"]}
                                    type="text"
                                    placeholder="이름, 전화번호 또는 파트너사 아이디로 검색"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <div className={style["container--members"]}>
                                    {filteredMembers.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                                    {filteredMembers.map((member) => (
                                        <MemberRow
                                            key={member.uid}
                                            member={member}
                                            selected={selectedMember?.uid === member.uid}
                                            onClick={() => setSelectedMember(member)}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className={style["empty-state"]}>파트너사를 선택해주세요.</div>
                        )}
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>지급 정보</h5>
                            <p>선택한 회원과 지급할 포인트를 확인합니다.</p>
                        </div>
                        <div className={style["selected-member-card"]}>
                            <span>선택 회원</span>
                            <strong>{selectedMember ? `${selectedMember.name} · ${formatPhone(selectedMember.phone)}` : "선택된 회원 없음"}</strong>
                        </div>
                        <input
                            type="number"
                            className={style["input--member-point"]}
                            placeholder="지급할 포인트 입력"
                            value={point ? point : ""}
                            onChange={(e) => setPoint(e.target.value ? parseInt(e.target.value) : 0)}
                        />
                        {message && <div className={style["status-message"]}>{message}</div>}
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={givePoint} disabled={loading}>
                        {loading ? "지급 중" : "지급하기"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const MemberRow = ({ member, selected, onClick }: { member: MemberType; selected: boolean; onClick: () => void }) => (
    <div className={[style["unit--member-list"], selected && style["unit--member-list--selected"]].filter(Boolean).join(" ")} onClick={onClick}>
        <div className={style["member-main"]}>
            <p className={style["row-strong"]}>{member.name}</p>
            <span>{member.phone ? formatPhone(member.phone) : "전화번호 없음"}</span>
        </div>
        <span className={[style["member-select-badge"], selected && style["member-select-badge--selected"]].filter(Boolean).join(" ")}>
            {selected ? "선택됨" : "선택"}
        </span>
    </div>
);

const useWithdrawMethods = (partners: PartnerType[]) => {
    const [withdrawMethods, setWithdrawMethods] = useState<WithdrawMethodType[]>([]);

    const partnerNameMap = useMemo(
        () =>
            partners.reduce<Record<string, string>>((acc, item) => {
                acc[item.id || item.docId] = item.name;
                return acc;
            }, {}),
        [partners]
    );

    useEffect(() => {
        const unsubscribe = onSnapshot(query(collectionGroup(db, "withdraw")), (snapshot) => {
            const list = snapshot.docs
                .map((document) => {
                    const partnerId = document.ref.parent.parent?.id || "";
                    const data = document.data() as Omit<WithdrawMethodType, "id" | "partnerId" | "partnerName">;

                    return {
                        ...data,
                        id: document.id,
                        partnerId,
                        partnerName: partnerNameMap[partnerId] || partnerId || "-",
                        active: data.active ?? true,
                    };
                })
                .sort((a, b) => {
                    const partnerCompare = a.partnerName.localeCompare(b.partnerName, "ko");
                    if (partnerCompare !== 0) return partnerCompare;
                    return Number(a.amount || 0) - Number(b.amount || 0);
                });

            setWithdrawMethods(list);
        });

        return () => unsubscribe();
    }, [partnerNameMap]);

    return { withdrawMethods };
};

const getActorMeta = (type: "created" | "updated") => {
    const currentUser = auth.currentUser;

    if (type === "created") {
        return {
            createdBy: currentUser?.uid || "",
            createdByEmail: currentUser?.email || null,
        };
    }

    return {
        updatedBy: currentUser?.uid || "",
        updatedByEmail: currentUser?.email || null,
    };
};

const getWithdrawMethodKey = (method: Pick<WithdrawMethodType, "partnerId" | "id">) => `${method.partnerId}:${method.id}`;

const getFirebaseErrorMessage = (err: unknown, fallback: string) => {
    const code = typeof err === "object" && err !== null && "code" in err ? String((err as { code?: string }).code) : "";
    if (code.includes("permission-denied") || code.includes("unauthorized")) return "총괄 관리자 권한이 필요합니다.";
    return fallback;
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-item"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const formatBankAccount = (bankAccount: any[] | undefined) => {
    if (!bankAccount || bankAccount.length === 0) return "등록된 계좌 정보가 없습니다.";

    const account = bankAccount[0];
    if (typeof account === "string") return account;
    if (!account || typeof account !== "object") return String(account);

    const bank = account.bank || account.bankName || account.name || "";
    const number = account.account || account.accountNumber || account.number || "";
    const owner = account.owner || account.holder || account.accountHolder || "";
    const parts = [bank, number, owner].filter(Boolean);

    return parts.length > 0 ? parts.join(" · ") : JSON.stringify(account);
};

const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleString("ko-KR");
};

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone || "";
};

export default Point;
